import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import EvidenceDetailPage from './components/EvidenceDetailPage';
import AssuranceReport from './components/AssuranceReport';
import ProgramsAssessment from './components/ProgramsAssessment';
import CoursesAssessment from './components/CoursesAssessment';
import StudentOutcomes from './components/StudentOutcomes';
import StudentOutcomesDetails_Program from './components/StudentOutcomesDetails_Program';
import StudentOutcomesDetails_Program_PLO from './components/StudentOutcomesDetails_Program_PLO';
import TeacherEvaluation from './components/TeacherEvaluation';
import AccreditationBank from './components/AccreditationBank';
import AddProgramCourses from './components/AddProgramCourses';
import ReportGeneration from './components/ReportGeneration';
import { SelectedTemplateProvider } from './context/SelectedTemplateContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserMenu from './components/UserMenu';

// Import dev helpers for testing in development
if (process.env.REACT_APP_DEV_MODE === 'true') {
  require('./utils/devHelpers');
}

// Wrapper component for AssuranceReport that handles navigation state
function AssuranceReportWrapper() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Helper function to get the current school from URL or use default
  const getSchoolFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('school') || 'UIUC';
  };

  const handleBackToDashboard = () => {
    // Navigate to Evidence Library
    const school = getSchoolFromUrl();
    navigate(`/evidence-library?school=${school}`);
  };

  const school = getSchoolFromUrl();
  
  // Extract props from navigation state or use sensible defaults
  const props = {
    onBack: handleBackToDashboard,
    school: school,
    // Only use defaults if no navigation state exists
    programName: location.state?.programName || location.state?.programData?.name || undefined,
    courseList: location.state?.courseList || [],
    sidebarExpanded: true,
    isCourse: location.state?.isCourse || false,
    returnedFromEvidence: location.state?.returnedFromEvidence || false,
    fullAlignmentData: location.state?.fullAlignmentData || null,
    originalTableData: location.state?.originalTableData || null,
    originalData: location.state?.originalData || null,
    initialTab: location.state?.initialTab || location.state?.tab || null,
    // Add returnedFromReportGeneration flag
    returnedFromReportGeneration: location.state?.returnedFromReportGeneration || false
  };

  return <AssuranceReport {...props} />;
}

// Wrapper component to access URL parameters
function AppContent() {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedPLO, setSelectedPLO] = useState(null);
  const [currentView, setCurrentView] = useState('main'); // 'main' or 'program-details' or 'plo-details'
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize activeNavItem based on current route
  const [activeNavItem, setActiveNavItem] = useState(() => {
    const path = location.pathname;
    if (path === '/') return 'Accreditation Bank';
    if (path === '/evidence-library') return 'Evidence Library';
    if (path === '/accreditation-bank') return 'Accreditation Bank';
    if (path === '/programs-assessment') return 'Programs Assessment';
    if (path === '/courses-assessment') return 'Courses Assessment';
    if (path === '/assessment') return 'Evidence Library';
    return 'Accreditation Bank'; // default fallback - Accreditation Bank is the home page
  });
  
  // Update activeNavItem when route changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setActiveNavItem('Accreditation Bank');
    } else if (path === '/evidence-library') {
      setActiveNavItem('Evidence Library');
    } else if (path === '/accreditation-bank') {
      setActiveNavItem('Accreditation Bank');
    } else if (path === '/programs-assessment') {
      setActiveNavItem('Programs Assessment');
    } else if (path === '/courses-assessment') {
      setActiveNavItem('Courses Assessment');
    } else if (path === '/assessment') {
      setActiveNavItem('Evidence Library');
    } else if (path === '/teacher-evaluation') {
      setActiveNavItem('Teacher Evaluation');
    }
    // Don't update for Student Outcomes as it's handled differently
  }, [location.pathname]);

  // Helper function to get the current school from URL or use default
  const getSchoolFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('school') || 'UIUC';
  };

  const handleSidebarToggle = (expanded) => {
    setSidebarExpanded(expanded);
  };

  const handleNavItemChange = (item) => {
    setActiveNavItem(item);
    setCurrentView('main'); // Reset to main view when changing navigation
  };

  const handleBackToDashboard = () => {
    setActiveNavItem('Evidence Library');
    setCurrentView('main');
    // Navigate to Evidence Library
    const school = getSchoolFromUrl();
    navigate(`/evidence-library?school=${school}`);
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    setCurrentView('program-details');
  };

  const handleBackToOutcomes = () => {
    setCurrentView('main');
    setSelectedPLO(null);
  };

  const handlePLOSelect = (plo) => {
    setSelectedPLO(plo);
    setCurrentView('plo-details');
  };

  const handleBackToProgram = () => {
    setCurrentView('program-details');
    setSelectedPLO(null);
  };

  // Get school parameter for passing to components
  const school = getSchoolFromUrl();

  return (
    <div className="app-container">
      <Sidebar 
        onToggle={handleSidebarToggle} 
        activeNavItem={activeNavItem}
        onNavItemChange={handleNavItemChange}
      />
      <div className={`main-content ${sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
        <div className="top-header">
          <UserMenu />
        </div>
        <div className="main-content-body">
        {activeNavItem === 'Student Outcomes' && (
          <ProtectedRoute>
          <>
            {currentView === 'plo-details' && selectedPLO && selectedProgram ? (
          <StudentOutcomesDetails_Program_PLO
            selectedProgram={selectedProgram}
            selectedPLO={selectedPLO}
            onBack={handleBackToProgram}
                school={school}
          />
            ) : currentView === 'program-details' && selectedProgram ? (
          <StudentOutcomesDetails_Program 
            selectedProgram={selectedProgram} 
            onBack={handleBackToOutcomes}
            onPLOSelect={handlePLOSelect}
                school={school}
          />
            ) : (
          <StudentOutcomes 
            sidebarExpanded={sidebarExpanded} 
            onBack={handleBackToDashboard}
            onProgramSelect={handleProgramSelect}
                school={school}
          />
            )}
          </>
          </ProtectedRoute>
        )}
        
        {activeNavItem !== 'Student Outcomes' && activeNavItem !== 'Teacher Evaluation' && (
          <ProtectedRoute>
            <Routes>
            <Route path="/" element={<AccreditationBank />} />
            <Route path="/evidence-library" element={<Dashboard sidebarExpanded={sidebarExpanded} school={school} />} />
            <Route path="/programs-assessment" element={<ProgramsAssessment onBack={handleBackToDashboard} sidebarExpanded={sidebarExpanded} school={school} />} />
            <Route path="/courses-assessment" element={<CoursesAssessment onBack={handleBackToDashboard} sidebarExpanded={sidebarExpanded} school={school} />} />
            <Route path="/assessment" element={<AssuranceReportWrapper />} />
            <Route path="/report-generation" element={<ReportGeneration />} />
            <Route path="/evidence-detail" element={<EvidenceDetailPage />} />
            <Route path="/accreditation-bank" element={<AccreditationBank />} />
            <Route path="/add-program-courses" element={<AddProgramCourses />} />
            <Route path="*" element={<Navigate to={`/?school=${school}`} replace />} />
          </Routes>
          </ProtectedRoute>
        )}

        {activeNavItem === 'Teacher Evaluation' && (
          <ProtectedRoute>
            <TeacherEvaluation />
          </ProtectedRoute>
        )}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SelectedTemplateProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </SelectedTemplateProvider>
    </AuthProvider>
  );
}

export default App; 